import { compact } from 'lodash';

import { DeleteButton } from '@/components/form/baseInputs/Button/DeleteButton';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { useUpdateHouseholdMutation } from '@/modules/household/graphql/Households.generated';

interface RemoveAssociatedUserButtonProps {
  userId: string;
  employeeId?: string;
  householdId: string;
  handleRemove: () => void;
  disabled?: boolean;
  onAfterRemove: () => void;
}

/**
 * @description This component is used to remove a user from a client. The user will be asked to confirm
 * the removal, and the user will be removed directly without any task reassignment workflow.
 */
export function RemoveAssociatedUserButton({
  householdId,
  userId,
  employeeId,
  disabled,
  handleRemove,
  onAfterRemove,
}: RemoveAssociatedUserButtonProps) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const [updateHousehold, { loading: updatingHousehold }] =
    useUpdateHouseholdMutation();

  function handleLocalConfirmDelete() {
    handleRemove();
    onAfterRemove();
  }

  function handleConfirmDelete() {
    return updateHousehold({
      variables: {
        id: householdId,
        input: {
          removeAssignedEmployeeIDs: compact([employeeId]),
          removeCollaboratorIDs: [userId],
        },
      },
      onCompleted: () => {
        showFeedback('Removed user access.', {
          variant: 'success',
        });
        handleLocalConfirmDelete();
      },
      onError: (err) => {
        showFeedback(
          'Could not remove user access. Please refresh the page and try again.'
        );
        reportError('failed to remove user access', err);
      },
    });
  }

  return (
    <DeleteButton
      loading={updatingHousehold}
      disabled={disabled}
      onConfirmDelete={handleConfirmDelete}
      promptDeleteText="Remove"
      confirmDeleteText="Confirm remove"
    />
  );
}
